import styled from 'styled-components';
import * as React from 'react';
import { VimeoEmbedProps } from './types';
import {
  VIMEO_ORIGIN,
  IFRAME_PLAYER_ID,
  getDefaultParams,
  buildEmbedUrl,
  isMutedFromParams,
  sendPlayAndUnmute,
  sendSetVolume as sendSetVolumeToPlayer,
  subscribeToPlayerEvents,
  parseVimeoMessage,
  createPlayClickHandler,
  PlayIcon,
} from './util';

export const VimeoEmbed: React.FC<VimeoEmbedProps> = ({
  videoId,
  src,
  params = getDefaultParams(),
  title = 'Vimeo video',
  className,
}) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const pendingPlayRef = React.useRef(false);
  const [showPlayOverlay, setShowPlayOverlay] = React.useState(true);
  const [isMuted, setIsMuted] = React.useState(() => isMutedFromParams(params));

  const embedUrl = buildEmbedUrl(videoId, src, params);

  React.useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== VIMEO_ORIGIN) return;
      const data = parseVimeoMessage(e.data);
      if (!data) return;
      const win = iframeRef.current?.contentWindow;

      if (data.method === 'ended' && data.value === true) {
        setShowPlayOverlay(true);
        return;
      }
      if (typeof data.event !== 'string') return;

      if (data.event === 'ready') {
        if (pendingPlayRef.current && win) {
          pendingPlayRef.current = false;
          sendPlayAndUnmute(win);
        }
        if (win) subscribeToPlayerEvents(win);
      } else if (data.event === 'play') {
        pendingPlayRef.current = false;
        setShowPlayOverlay(false);
      } else if (data.event === 'pause' || data.event === 'finish' || data.event === 'ended') {
        setShowPlayOverlay(true);
      }
    };

    window.addEventListener('message', handleMessage);
    let interval: ReturnType<typeof setInterval> | null = null;
    if (!showPlayOverlay) {
      interval = setInterval(() => {
        if (!iframeRef.current?.contentWindow) return;
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ method: 'ended' }),
          VIMEO_ORIGIN,
        );
      }, 1500);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (interval) clearInterval(interval);
    };
  }, [showPlayOverlay]);

  const handlePlayClick = React.useMemo(
    () =>
      createPlayClickHandler({
        getWindow: () => iframeRef.current?.contentWindow,
        setShowPlayOverlay,
        setIsMuted,
        pendingPlayRef,
      }),
    [],
  );

  /*   const sendSetVolume = (volume: number) => {
      const win = iframeRef.current?.contentWindow;
      if (win) sendSetVolumeToPlayer(win, volume);
      setIsMuted(volume === 0);
    }; */

  if (!embedUrl) return null;

  return (
    <VideoWrapper className={className}>
      <iframe
        id={IFRAME_PLAYER_ID}
        ref={iframeRef}
        src={embedUrl}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title={title}
      />
      {showPlayOverlay && (
        <PlayOverlay onClick={handlePlayClick} aria-label="Spill av video" title="Spill av video">
          <PlayButtonCircle>
            <PlayIcon />
          </PlayButtonCircle>
        </PlayOverlay>
      )}
      {/*       <SoundButton
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          sendSetVolume(isMuted ? 1 : 0);
        }}
        aria-label={isMuted ? 'Slå på lyd' : 'Slå av lyd'}
        title={isMuted ? 'Slå på lyd' : 'Slå av lyd'}
      >
        {isMuted ? <SpeakerMutedIcon /> : <SpeakerIcon />}
      </SoundButton> */}
    </VideoWrapper>
  );
};

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  line-height: 0;

  /* Fallback for miljøer der aspect-ratio ikke støttes/virker som forventet */
  @supports not (aspect-ratio: 16 / 9) {
    &::before {
      content: '';
      display: block;
      padding-top: 56.25%;
    }
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const PlayOverlay = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1;
`;

/** Vimeo-lignende sirkel: hvit, lett skygge, ~68px; mørk play-ikon */
const PlayButtonCircle = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #171719;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  color: #e7cfa7;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  ${PlayOverlay}:hover & {
    background: #171719;
  }
`;

/* const SoundButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #171719;
  color: #e7cfa7;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

function SpeakerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    </svg>
  );
}

function SpeakerMutedIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
    </svg>
  );
} */
